import { Button, Grid, makeStyles, MenuItem, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import moment from "moment/moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getMessagesGeneral, getRecipients } from "../../actions/messageCenterActions";
import { showAlertSnackbar } from "../../reducers/uiSlice";
import { getUserPreference } from "../../utilities/common";
import FullscreenLoading from "../Common/FullscreenLoading";
import _ from "lodash";

function ConditionalSearch(props) {
    const {t} = useTranslation('messageCenter');
    const dispatch = useDispatch();
    const userPref = getUserPreference();

    const INIT_PARAMS = {
        warehouse: userPref.userCity,
        posted_from: moment().format('YYYY-MM-DD'),
        posted_to: moment().format('YYYY-MM-DD'),
        type_code: '0',
        recipient_type: '1',
        recipient: '0'
    }

    const RECIPIENTS_INIT = {
        code: 0,
        name: t('common:all')
    };

    const MAX_SEARCH_DURATION = 90;

    const messageTypes = useSelector(state => state.messageCenterReducer.getMessageTypes);
    const recipientGroup = useSelector(state => state.messageCenterReducer.getRecipientGroup);
    const recipientsAll = useSelector(state => state.messageCenterReducer.getRecipientsAll);
    const loadingOpen = useSelector(state => state.messageCenterReducer.getMessagesGeneralLoading);

    const [params, setParams] = useState(INIT_PARAMS);
    const [recipientType, setRecipientType] = useState('1'); // 0 => all
    const [recipients, setRecipients] = useState(recipientsAll);
    const [loading, setLoading] = useState(false);
    const [recipientsSelected, setRecipientsSelected] = useState([]);

    const handleSearch = () => {
        if(moment.duration(moment(params.posted_to).diff(moment(params.posted_from))).asDays() > MAX_SEARCH_DURATION) {
            dispatch(showAlertSnackbar({message: t('more_than_90'), type: 'warning'}));
            return;
        }
        let paramsModify = {};
        for (const key in params) {
            if(key === 'recipient') {
                if(recipientsSelected?.length > 0) {
                    const value = _.map(recipientsSelected, 'code')?.toString();
                    paramsModify[key] = value;
                }
            } else {
                if(!(key === 'type_code' && params[key] === '0')) {
                    paramsModify[key] = params[key];
                }
            }
        }
        paramsModify['recipient_type'] = recipientType;
        props.handleSearch(paramsModify);
    }

    useEffect(() => {
        // '0' -> all
        // '1' -> warehouse
        // '2' -> group
        // '3' -> individual
        setLoading(true);
        setRecipientsSelected([]);
        dispatch(getRecipients(recipientType, userPref.userCity))
        .then((res) => {
            let recipientsModify = [];
            if(recipientType === '0') {
                recipientsModify = res;
            } else {
                recipientsModify.push(RECIPIENTS_INIT);
                res.forEach(item => {
                    recipientsModify.push(recipientType === '1' ? {
                        code: item.warehouse_id,
                        name: item.warehouse_name
                    } : (recipientType === '2' ? {
                        code: item.team_id,
                        name: item.group_name
                    } : {
                        code: item.driver_id + '',
                        name: item.driver_id + ''
                    }))
                });
            }
            setRecipients(recipientsModify);
        })
        .finally(() => {
            setLoading(false);
        })
    }, [recipientType])


    return (
        <Grid container spacing={2}>
            <Grid item xs={3}>
                <TextField
                    label={t('message_type')}
                    select
                    fullWidth
                    variant="outlined"
                    value={params.type_code}
                    onChange={(e) => setParams((prev) => ({
                        ...prev,
                        'type_code': e.target.value
                    }))}
                >
                    <MenuItem value={'0'}>{t('common:all')}</MenuItem>
                    {
                        messageTypes?.map((message) => (
                            <MenuItem value={message.code}>{message.name}</MenuItem>
                        ))
                    }
                </TextField>
            </Grid>
            <Grid item xs={3}>
                <TextField
                    label={t('recipient_group')}
                    select
                    fullWidth
                    variant="outlined"
                    value={recipientType}
                    onChange={(e) => setRecipientType(e.target.value)}
                >
                    {
                        recipientGroup?.map((group) => (
                            group.code !== '0' &&
                            <MenuItem value={group.code}>{group.name}</MenuItem>
                        ))
                    }
                </TextField>
            </Grid>
            <Grid item xs={6}>
                <Autocomplete
                    id='messagecenter-messagerecipients-search-autocomplete'
                    multiple
                    options={recipients || []}
                    getOptionLabel={recipient => recipient.name}
                    filterSelectedOptions
                    limitTags={1}
                    onChange={(event, value) => {
                        if(value.length) {
                            let isAll = false;
                            value.forEach(item => {
                                if(item.code === RECIPIENTS_INIT.code) {
                                    isAll = true;
                                    return;
                                }
                            });
                            if(isAll) {
                                setRecipientsSelected([RECIPIENTS_INIT]);
                                return;
                            }
                        }
                        setRecipientsSelected(value);
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label={t('recipients')}
                        />
                    )}
                    fullWidth
                    value={recipientsSelected}
                />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    label={t('date_from')}
                    type="date"
                    fullWidth
                    variant="outlined"
                    value={params.posted_from}
                    onChange={(e) => setParams((prev) => ({
                        ...prev,
                        'posted_from': e.target.value
                    }))}
                />
            </Grid>
            <Grid item xs={3}>
                <TextField
                    label={t('date_to')}
                    type="date"
                    fullWidth
                    variant="outlined"
                    value={params.posted_to}
                    onChange={(e) => setParams((prev) => ({
                        ...prev,
                        'posted_to': e.target.value
                    }))}
                />
            </Grid>
            <Grid item xs={5}>
                <TextField
                    label={t('title')}
                    fullWidth
                    variant="outlined"
                    value={params.title}
                    onChange={(e) => setParams((prev) => ({
                        ...prev,
                        'title': e.target.value
                    }))}
                />
            </Grid>
            <Grid item xs={1}>
                <Button
                    color='primary' 
                    variant='contained'
                    fullWidth
                    onClick={handleSearch}
                >
                    {t('common:search')}
                </Button>
            </Grid>
            <FullscreenLoading open={loadingOpen || loading} />
        </Grid>
    );
}
export default ConditionalSearch;